/* eslint-disable @typescript-eslint/no-explicit-any */
import { Chat } from '../models';
import { Prettify } from '../utils/types';
import { s } from '../schema';
import { select } from '../utils/micro-ngrx';
import * as fromConfig from './config.reducer';
import * as fromMessages from './messages.reducer';
import * as fromStatus from './status.reducer';
import * as fromStreamingMessage from './streaming-message.reducer';
import * as fromToolCalls from './tool-calls.reducer';
import * as fromTools from './tools.reducer';
import * as fromThread from './thread.reducer';

export const reducers = {
  config: fromConfig.reducer,
  messages: fromMessages.reducer,
  status: fromStatus.reducer,
  streamingMessage: fromStreamingMessage.reducer,
  toolCalls: fromToolCalls.reducer,
  tools: fromTools.reducer,
  thread: fromThread.reducer,
};

type State = Prettify<{
  [P in keyof typeof reducers]: ReturnType<(typeof reducers)[P]>;
}>;

function applyEmulatedOutputToMessage(
  message: Chat.Internal.Message,
  toolCalls: Record<string, Chat.Internal.ToolCall>,
  responseSchema: s.HashbrownType | undefined,
  emulateStructuredOutput: boolean | undefined,
): Chat.Internal.Message {
  if (!emulateStructuredOutput || !responseSchema) {
    return message;
  }

  if (message.role !== 'assistant') {
    return message;
  }

  const outputToolCall = message.toolCallIds
    .map((toolCallId) => toolCalls[toolCallId])
    .find((toolCall) => toolCall?.name === 'output');

  if (!outputToolCall) {
    return message;
  }

  const rawArguments = outputToolCall.arguments;
  const content =
    typeof rawArguments === 'string'
      ? rawArguments
      : JSON.stringify(rawArguments);
  const contentResolved =
    outputToolCall.argumentsResolved ?? message.contentResolved;

  return {
    ...message,
    content,
    contentResolved,
  };
}

/**
 * Messages
 */
export const selectMessagesState = (state: State) => state.messages;
export const selectMessages = select(
  selectMessagesState,
  fromMessages.selectMessages,
);

/**
 * Status
 */
export const selectStatusState = (state: State) => state.status;
export const selectIsReceiving = select(
  selectStatusState,
  fromStatus.selectIsReceiving,
);
export const selectIsSending = select(
  selectStatusState,
  fromStatus.selectIsSending,
);
export const selectIsGenerating = select(
  selectStatusState,
  fromStatus.selectIsGenerating,
);
export const selectSendingError = select(
  selectStatusState,
  fromStatus.selectSendingError,
);
export const selectGeneratingError = select(
  selectStatusState,
  fromStatus.selectGeneratingError,
);
export const selectError = select(selectStatusState, fromStatus.selectError);

export const selectExhaustedRetries = select(
  selectStatusState,
  fromStatus.selectExhaustedRetries,
);

/**
 * Streaming Message
 */
export const selectStreamingMessageState = (state: State) =>
  state.streamingMessage;
export const selectRawStreamingMessage = select(
  selectStreamingMessageState,
  fromStreamingMessage.selectRawStreamingMessage,
);
export const selectRawStreamingToolCalls = select(
  selectStreamingMessageState,
  fromStreamingMessage.selectRawStreamingToolCalls,
);
export const selectStreamingMessageError = select(
  selectStreamingMessageState,
  fromStreamingMessage.selectStreamingMessageError,
);
export const selectStreamingMessage = select(
  selectStreamingMessageState,
  fromStreamingMessage.selectStreamingMessage,
);
export const selectStreamingToolCallEntities = select(
  selectStreamingMessageState,
  fromStreamingMessage.selectStreamingToolCallEntities,
);

/**
 * Tools
 */
export const selectToolsState = (state: State) => state.tools;
export const selectTools = select(selectToolsState, fromTools.selectTools);
export const selectToolEntities = select(
  selectToolsState,
  fromTools.selectToolEntities,
);

/**
 * Tool Calls
 */
export const selectToolCallsState = (state: State) => state.toolCalls;
export const selectToolCalls = select(
  selectToolCallsState,
  fromToolCalls.selectToolCalls,
);
export const selectToolCallEntities = select(
  selectToolCallsState,
  fromToolCalls.selectToolCallEntities,
);
export const selectPendingToolCalls = select(
  selectToolCallsState,
  fromToolCalls.selectPendingToolCalls,
);

/**
 * Thread
 */
export const selectThreadState = (state: State) => state.thread;
export const selectThreadIdState = select(
  selectThreadState,
  fromThread.selectThreadId,
);
export const selectIsLoadingThread = select(
  selectThreadState,
  fromThread.selectIsLoadingThread,
);
export const selectIsSavingThread = select(
  selectThreadState,
  fromThread.selectIsSavingThread,
);
export const selectThreadLoadError = select(
  selectThreadState,
  fromThread.selectThreadLoadError,
);
export const selectThreadSaveError = select(
  selectThreadState,
  fromThread.selectThreadSaveError,
);

/**
 * Config
 */
export const selectConfigState = (state: State) => state.config;
export const selectApiUrl = select(selectConfigState, fromConfig.selectApiUrl);
export const selectMiddleware = select(
  selectConfigState,
  fromConfig.selectMiddleware,
);
export const selectModel = select(selectConfigState, fromConfig.selectModel);
export const selectSystem = select(selectConfigState, fromConfig.selectSystem);
export const selectDebounce = select(
  selectConfigState,
  fromConfig.selectDebounce,
);
export const selectRetries = select(
  selectConfigState,
  fromConfig.selectRetries,
);
export const selectThreadId = select(
  selectThreadState,
  fromThread.selectThreadId,
);
export const selectResponseSchema = select(
  selectConfigState,
  fromConfig.selectResponseSchema,
);
export const selectStructuredOutput = select(
  selectConfigState,
  fromConfig.selectStructuredOutput,
);
export const selectEmulateStructuredOutput = select(
  selectConfigState,
  fromConfig.selectEmulateStructuredOutput,
);
export const selectTransport = select(
  selectConfigState,
  fromConfig.selectTransport,
);
export const selectUiRequested = select(
  selectConfigState,
  fromConfig.selectUiRequested,
);

/**
 * Top-level selectors
 */
const selectNonStreamingViewMessages = select(
  selectMessages,
  selectToolCallEntities,
  selectTools,
  selectResponseSchema,
  selectEmulateStructuredOutput,
  (messages, toolCalls, tools, responseSchema, emulateStructuredOutput) => {
    return messages.flatMap((message): Chat.AnyMessage[] =>
      Chat.helpers.toViewMessagesFromInternal(
        applyEmulatedOutputToMessage(
          message,
          toolCalls,
          responseSchema,
          emulateStructuredOutput,
        ),
        toolCalls,
        tools,
        responseSchema,
      ),
    );
  },
);

const selectStreamingViewMessages = select(
  selectStreamingMessage,
  selectStreamingToolCallEntities,
  selectTools,
  selectResponseSchema,
  selectEmulateStructuredOutput,
  (
    streamingMessage,
    streamingToolCalls,
    tools,
    responseSchema,
    emulateStructuredOutput,
  ) => {
    return (streamingMessage ? [streamingMessage] : []).flatMap(
      (message): Chat.AnyMessage[] =>
        Chat.helpers.toViewMessagesFromInternal(
          applyEmulatedOutputToMessage(
            message,
            streamingToolCalls,
            responseSchema,
            emulateStructuredOutput,
          ),
          streamingToolCalls,
          tools,
          responseSchema,
        ),
    );
  },
);

export const selectViewMessages = select(
  selectNonStreamingViewMessages,
  selectStreamingViewMessages,
  (nonStreamingMessages, streamingMessages) => {
    return [...nonStreamingMessages, ...streamingMessages];
  },
);

export const selectLastAssistantMessage = select(
  selectViewMessages,
  (messages): Chat.AssistantMessage<any, any> | undefined => {
    return messages.findLast((message) => message.role === 'assistant');
  },
);

export const selectApiMessages = select(
  selectMessages,
  selectToolCalls,
  (messages, toolCalls): Chat.Api.Message[] => {
    return messages.flatMap((message): Chat.Api.Message[] =>
      Chat.helpers.toApiMessagesFromInternal(message, toolCalls),
    );
  },
);

export const selectShouldGenerateMessage = select(
  selectApiMessages,
  (messages) => {
    const lastMessage = messages[messages.length - 1];

    if (!lastMessage) {
      return false;
    }

    return lastMessage.role === 'user' || lastMessage.role === 'tool';
  },
);

export const selectApiTools = select(
  selectTools,
  selectResponseSchema,
  selectEmulateStructuredOutput,
  (tools, responseSchema, emulateStructuredOutput) => {
    return Chat.helpers.toApiToolsFromInternal(
      tools,
      emulateStructuredOutput && !!responseSchema,
      responseSchema ?? s.nullish(),
    );
  },
);

export const selectUnifiedError = select(
  selectSendingError,
  selectGeneratingError,
  selectThreadLoadError,
  selectThreadSaveError,
  selectError,
  (
    sendingError,
    generatingError,
    threadLoadError,
    threadSaveError,
    statusError,
  ) =>
    sendingError ??
    generatingError ??
    (threadLoadError && new Error(threadLoadError.error)) ??
    (threadSaveError && new Error(threadSaveError.error)) ??
    statusError,
);

export const selectIsRunningToolCalls = select(
  selectPendingToolCalls,
  selectIsGenerating,
  selectIsLoadingThread,
  selectIsSavingThread,
  selectUnifiedError,
  (pendingToolCalls, isGenerating, isLoadingThread, isSavingThread, error) =>
    pendingToolCalls.length > 0 &&
    !isGenerating &&
    !isLoadingThread &&
    !isSavingThread &&
    !error,
);

export const selectIsLoading = select(
  selectIsSending,
  selectIsGenerating,
  selectIsReceiving,
  selectIsRunningToolCalls,
  selectIsLoadingThread,
  selectIsSavingThread,
  (
    isSending,
    isGenerating,
    isReceiving,
    isRunningToolCalls,
    isLoadingThread,
    isSavingThread,
  ) =>
    isSending ||
    isGenerating ||
    isReceiving ||
    isRunningToolCalls ||
    isLoadingThread ||
    isSavingThread,
);
